import { z } from "zod";
import { AgentId, AgentVersionId, CatalogSlug, PaginationQuery } from "../../common";

export const AgentListing = z
  .object({
    id: AgentId,
    slug: CatalogSlug,
    liveVersionId: AgentVersionId,
    name: z.string().min(1).max(200).meta({
      description: "The live version's display name.",
      example: "GPT-6 Luna",
    }),
    description: z.string().max(10_000).meta({
      description: "What the live version does.",
      example: "Writes each round with a language model, a shell, and an atlas.",
    }),
    models: z.array(z.string().min(1).max(200)).meta({
      description: "Models the live version calls, for display only.",
      example: ["openai/gpt-6-luna"],
    }),
  })
  .meta({
    id: "AgentListing",
    description: "A published Game Master whose live version can start a game.",
  });
export type AgentListing = z.infer<typeof AgentListing>;

export const ListAgentsQuery = PaginationQuery;
export type ListAgentsQuery = z.infer<typeof ListAgentsQuery>;

export const ListAgentsResponse = z
  .object({
    items: z.array(AgentListing),
    nextCursor: z.string().nullable(),
  })
  .meta({
    id: "ListAgentsResponse",
    description: "One page of published agents available to start a game.",
    example: { items: [], nextCursor: null },
  });
export type ListAgentsResponse = z.infer<typeof ListAgentsResponse>;

export const AgentVersionParams = z.strictObject({
  versionId: AgentVersionId,
});
export type AgentVersionParams = z.infer<typeof AgentVersionParams>;

export const AgentVersionFileParams = AgentVersionParams.extend({
  file: z
    .string()
    .min(1)
    .max(1024)
    .refine(
      path =>
        path
          .split("/")
          .every(
            part => /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(part) && part !== "." && part !== "..",
          ),
      "A bundle file needs a relative path.",
    ),
});
export type AgentVersionFileParams = z.infer<typeof AgentVersionFileParams>;
